import { motion } from 'framer-motion'
import { Target, Users, Lightbulb, Award } from 'lucide-react'

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Innovation",
      description: "Driving cutting-edge solutions that transform businesses and industries."
    },
    {
      icon: Users,
      title: "Leadership",
      description: "Building and mentoring high-performing teams to achieve exceptional results."
    },
    {
      icon: Lightbulb,
      title: "Strategy",
      description: "Developing comprehensive technology strategies that align with business goals."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Delivering world-class solutions with attention to detail and quality."
    }
  ]

  return (
    <section id="about" className="section-padding bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            About Me
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-indigo-600 mx-auto mb-8" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-playfair font-semibold text-gray-900 mb-6">
              Technology Leader & Innovation Driver
            </h3>
            
            <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
              <p>
                As the Founder of NovoUp Technologies, I leverage my 20+ years of expertise in Computer Science and technology leadership to drive innovation in InsurTech, FinTech, HealthTech, and eCommerce, harnessing the power of AI, ML, and GenAI. My journey melds strategic foresight with a profound engagement in transformative technologies.
              </p>
              
              <p>
                My career, rooted in project management, software engineering, and information security, now focuses on leveraging AI and ML to revolutionize operations in various sectors, including InsurTech, FinTech, HealthTech, and eCommerce. My role in cloud-enabled application development and high-impact projects has always been underpinned by a commitment to integrating next-gen technologies for enhanced security and efficiency.
              </p>
              
              <p>
                I specialize in aligning C-level strategic goals with technical product development, particularly in areas like machine learning and generative AI. This involves not only leading teams in developing AI-driven solutions but also ensuring these technologies align with overarching business strategies.
              </p>
              
              <p>
                At NovoUp, I am dedicated to crafting advanced solutions using AI and ML, focusing on real-time data processing, automated architecture governance, and fostering an innovative environment. Our goal is to create cloud-native applications that not only process data efficiently but also utilize GenAI for predictive analysis and enhanced decision-making.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <div className="bg-white px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                AI & Machine Learning
              </div>
              <div className="bg-white px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                InsurTech & FinTech
              </div>
              <div className="bg-white px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                HealthTech & eCommerce
              </div>
              <div className="bg-white px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                Cloud Architecture
              </div>
              <div className="bg-white px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                GenAI & Predictive Analytics
              </div>
              <div className="bg-white px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                NovoUp Technologies
              </div>
            </motion.div>
          </motion.div>

          {/* Visual Element */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-primary-600 to-indigo-600 rounded-2xl p-8 text-white">
              <h4 className="text-2xl font-playfair font-semibold mb-4">Mission</h4>
              <p className="text-lg leading-relaxed">
                To empower organizations through innovative technology solutions that drive 
                growth, efficiency, and competitive advantage in the digital age.
              </p>
              
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">100%</div>
                  <div className="text-sm opacity-90">Client Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-sm opacity-90">Support Available</div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full opacity-80" />
            <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-pink-400 rounded-full opacity-80" />
          </motion.div>
        </div>

        {/* Values Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-sm hover-lift text-center"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-indigo-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                <value.icon className="text-white" size={24} />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h4>
              <p className="text-gray-600">{value.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default About
